import { useNavigate } from 'react-router-dom';
import {
  Ban,
  ClipboardCopy,
  CopyPlus,
  CreditCard,
  Eye,
  FileKey2,
  HardDrive,
  LogIn,
  Network,
  PackageOpen,
  Snowflake,
  Trash2,
  Undo2,
  Users,
} from 'lucide-react';
import type { LifecycleDto } from '@his/shared';
import { copyText } from '@/lib/utils';
import { ActionMenu, ActionMenuItem, ActionMenuSeparator } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { useOwnerCapability } from './owner-nav';

export interface FacilityRowFacts {
  id: string;
  name: string;
  slug: string;
  status: string;
  deploymentMode: 'CLOUD' | 'LOCAL';
  offlineEnabled: boolean;
  lifecycle?: LifecycleDto | null;
  closureEndsAt?: string | null;
}

export function installInstructions(facility: Pick<FacilityRowFacts, 'name' | 'slug'>, origin: string): string {
  return [
    `Installing ${facility.name} on its own server`,
    '',
    `1. On the server, open ${origin}/ and choose "Download for offline use".`,
    `2. Enter the subdomain "${facility.slug}" and the download PIN from Settings → About (a Super Admin can generate one).`,
    '3. Run the installer and activate it with the enrolment code the platform gives you.',
    '4. Keep the server connected at least once every 7 days so its licence stays current.',
  ].join('\n');
}

export function facilityActionBlock(facility: FacilityRowFacts): string | null {
  const state = facility.lifecycle?.state;
  if (state === 'deletion_grace') return 'Waiting for deletion — cancel the closure first';
  if (state === 'archived') return 'Archived — restore it first';
  return null;
}

export function FacilityRowActions({
  facility,
  onGroup,
  onCopyInto,
}: {
  facility: FacilityRowFacts;
  onGroup: () => void;
  onCopyInto: () => void;
}) {
  const navigate = useNavigate();
  const toast = useToast();
  const canSignInAs = useOwnerCapability('tenant.sign_in_as');
  const canDeployment = useOwnerCapability('deployment.read');
  const canUsers = useOwnerCapability('tenant.user.read');
  const canFreeze = useOwnerCapability('tenant.freeze');
  const canSuspend = useOwnerCapability('tenant.suspend');
  const canGroup = useOwnerCapability('tenant.group.update');
  const canCopy = useOwnerCapability('tenant.copy_masters');
  const canExport = useOwnerCapability('tenant.export');
  const canClose = useOwnerCapability('tenant.closure');
  const go = (tab: string, action?: string) => navigate(`/owner/tenants/${facility.id}?tab=${tab}${action ? `&action=${action}` : ''}`);
  const blocked = facilityActionBlock(facility);
  const suspended = facility.lifecycle?.state === 'suspended' || facility.status === 'suspended';
  const closing = facility.lifecycle?.state === 'closing' || facility.lifecycle?.state === 'deletion_grace' || Boolean(facility.closureEndsAt);

  return (
    <ActionMenu label={`Actions for ${facility.name}`}>
      <ActionMenuItem icon={<Eye className="h-4 w-4" />} onSelect={() => go('summary')}>
        Open
      </ActionMenuItem>
      {canSignInAs && (
        <ActionMenuItem icon={<LogIn className="h-4 w-4" />} disabledReason={blocked} onSelect={() => go('users')}>
          Sign in as…
        </ActionMenuItem>
      )}
      {canDeployment && (
        <ActionMenuItem icon={<HardDrive className="h-4 w-4" />} onSelect={() => go('deployment')}>
          Deployment…
        </ActionMenuItem>
      )}
      {canDeployment && (
        <ActionMenuItem
          icon={<ClipboardCopy className="h-4 w-4" />}
          disabledReason={facility.offlineEnabled ? null : 'Offline mode is off for this facility'}
          onSelect={() => {
            void copyText(installInstructions(facility, window.location.origin)).then((copied) =>
              copied ? toast.success('Install instructions copied') : toast.error('Could not copy', 'Select the text on the Deployment tab instead.'),
            );
          }}
        >
          Copy install instructions
        </ActionMenuItem>
      )}
      <ActionMenuSeparator />
      <ActionMenuItem icon={<FileKey2 className="h-4 w-4" />} onSelect={() => go('licence')}>
        Licence…
      </ActionMenuItem>
      <ActionMenuItem icon={<CreditCard className="h-4 w-4" />} onSelect={() => go('account')}>
        Subscription…
      </ActionMenuItem>
      {canFreeze && !suspended && (
        <ActionMenuItem icon={<Snowflake className="h-4 w-4" />} disabledReason={blocked} onSelect={() => go('summary', 'freeze')}>
          Freeze…
        </ActionMenuItem>
      )}
      {canSuspend && (
        <ActionMenuItem
          icon={suspended ? <Undo2 className="h-4 w-4" /> : <Ban className="h-4 w-4" />}
          destructive={!suspended}
          disabledReason={blocked}
          onSelect={() => go('summary', suspended ? 'reactivate' : 'suspend')}
        >
          {suspended ? 'Reactivate…' : 'Suspend…'}
        </ActionMenuItem>
      )}
      {canGroup && (
        <ActionMenuItem icon={<Network className="h-4 w-4" />} onSelect={onGroup}>
          Group & sharing…
        </ActionMenuItem>
      )}
      {canCopy && (
        <ActionMenuItem
          icon={<CopyPlus className="h-4 w-4" />}
          disabledReason={facility.deploymentMode === 'LOCAL' ? 'Runs on its own server — copy data there instead' : blocked}
          onSelect={onCopyInto}
        >
          Copy data from…
        </ActionMenuItem>
      )}
      {canUsers && (
        <ActionMenuItem icon={<Users className="h-4 w-4" />} onSelect={() => go('users')}>
          Users & access
        </ActionMenuItem>
      )}
      {canExport && (
        <ActionMenuItem icon={<PackageOpen className="h-4 w-4" />} onSelect={() => go('data')}>
          Export data
        </ActionMenuItem>
      )}
      {canClose && (
        <>
          <ActionMenuSeparator />
          <ActionMenuItem icon={closing ? <Undo2 className="h-4 w-4" /> : <Trash2 className="h-4 w-4" />} destructive={!closing} onSelect={() => go('data')}>
            {closing ? 'Cancel closure…' : 'Close account…'}
          </ActionMenuItem>
        </>
      )}
    </ActionMenu>
  );
}
